import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { Calendar, CheckCircle, XCircle, Save, ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AttendanceSheet() {
    const { mappingId } = useParams();
    const navigate = useNavigate();
    const { mappings, fetchStudents, markAttendance, fetchAttendance } = useData();

    const [students, setStudents] = useState([]);
    const [attendanceState, setAttendanceState] = useState({}); // { studentId: 'PRESENT' | 'ABSENT' }
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [mappingDetails, setMappingDetails] = useState(null);
    const [existingRecord, setExistingRecord] = useState(false);

    useEffect(() => {
        const mapping = mappings.find(m => m.id === parseInt(mappingId));
        if (mapping) {
            setMappingDetails(mapping);
            loadData(mapping);
        }
    }, [mappingId, mappings, date]);

    const loadData = async (mapping) => {
        setLoading(true);
        try {
            const [studentList, attendanceList] = await Promise.all([
                fetchStudents(mapping.sectionId),
                fetchAttendance(mapping.id, date)
            ]);

            setStudents(studentList);

            // Initialize state
            const initialState = {};
            if (attendanceList.length > 0) {
                setExistingRecord(true);
                attendanceList.forEach(rec => {
                    initialState[rec.studentId] = rec.status;
                });
            } else {
                setExistingRecord(false);
                // Default all to PRESENT
                studentList.forEach(s => {
                    initialState[s.id] = 'PRESENT';
                });
            }
            setAttendanceState(initialState);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const toggleStatus = (studentId) => {
        setAttendanceState(prev => ({
            ...prev,
            [studentId]: prev[studentId] === 'PRESENT' ? 'ABSENT' : 'PRESENT'
        }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const records = Object.entries(attendanceState).map(([sid, status]) => ({
            studentId: parseInt(sid),
            status
        }));

        const success = await markAttendance(date, mappingId, records);
        if (success) {
            alert('Attendance saved successfully!');
            setExistingRecord(true);
        } else {
            alert('Failed to save attendance.');
        }
        setSubmitting(false);
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">Loading student list...</div>;
    if (!mappingDetails) return <div className="p-8 text-center text-red-500">Class not found.</div>;

    const presentCount = Object.values(attendanceState).filter(s => s === 'PRESENT').length;
    const absentCount = Object.values(attendanceState).filter(s => s === 'ABSENT').length;

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-secondary rounded-full transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold font-heading text-primary">{mappingDetails.subjectName}</h1>
                    <p className="text-muted-foreground">{mappingDetails.sectionName} • {mappingDetails.subjectCode}</p>
                </div>
            </div>

            {existingRecord && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 p-4 rounded-xl flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span className="font-medium">Attendance has already been marked for this date. You can edit it below.</span>
                </div>
            )}

            <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 sticky top-4 z-10 backdrop-blur-xl bg-card/95">
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="flex items-center gap-2 bg-secondary px-4 py-2 rounded-lg w-full sm:w-auto">
                        <Calendar size={18} className="text-muted-foreground" />
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="bg-transparent border-none outline-none text-sm font-bold w-full"
                        />
                    </div>
                    <div className="flex items-center gap-6 text-xs font-bold uppercase tracking-wider">
                        <div className="text-emerald-600 flex items-center gap-1.5 border-b-2 border-emerald-500/20 pb-0.5">
                            <CheckCircle size={14} /> {presentCount} Present
                        </div>
                        <div className="text-rose-500 flex items-center gap-1.5 border-b-2 border-rose-500/20 pb-0.5">
                            <XCircle size={14} /> {absentCount} Absent
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full md:w-auto bg-primary text-primary-foreground px-8 py-3 rounded-xl font-bold shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {submitting ? 'Saving...' : (
                        <>
                            <Save size={18} />
                            <span>Apply Attendance</span>
                        </>
                    )}
                </button>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="grid grid-cols-[60px_1fr_100px] md:grid-cols-[100px_1fr_140px] bg-secondary p-4 text-[10px] md:text-xs font-black text-muted-foreground uppercase tracking-widest border-b border-border">
                    <div className="text-center">Roll</div>
                    <div>Student Identity</div>
                    <div className="text-center">Status</div>
                </div>

                <div className="divide-y divide-border">
                    {students.map((student, idx) => {
                        const isPresent = attendanceState[student.id] === 'PRESENT';
                        return (
                            <div key={student.id} className="grid grid-cols-[60px_1fr_100px] md:grid-cols-[100px_1fr_140px] p-3 md:p-4 items-center hover:bg-muted/5 transition-colors">
                                <div className="text-center font-mono text-[10px] md:text-xs text-muted-foreground font-bold">{student.rollNumber.split('-').pop()}</div>
                                <div
                                    className="font-bold text-foreground cursor-pointer hover:underline hover:text-primary text-sm md:text-base truncate pr-2"
                                    onClick={() => navigate(`/dashboard/students/${student.id}`)}
                                >
                                    {student.name}
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => toggleStatus(student.id)}
                                        className={cn(
                                            "flex items-center gap-1 md:gap-2 px-2 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-bold transition-all w-full md:w-32 justify-center",
                                            isPresent
                                                ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                                                : "bg-rose-500 text-white shadow-lg shadow-rose-500/20"
                                        )}
                                    >
                                        {isPresent ? <CheckCircle size={14} className="md:size-[16px]" /> : <XCircle size={14} className="md:size-[16px]" />}
                                        <span>{isPresent ? 'Present' : 'Absent'}</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
